import type { RateRule, UserTier } from '../dataProvider';
import { regionTarget, type RegionScopeView } from '../rateRules/rateRuleScope';
import {
  resolveEffectiveRate,
  type ResolutionResult,
} from '../rateRules/resolveEffectiveRate';

/**
 * The store-card rate projection (D-page-schema-editor-8c-boost). The ONE place the
 * two novel Phase 8 surfaces connect: the schema preview's store cards read the #64
 * rate-rules resolver. This is a PROJECTION over today's rules and catalog (the one
 * resolver), NOT the live per-click resolution and NOT a claim of a specific member's
 * realised rate.
 *
 * BRAND-LEVEL (region-level) resolution. A preview card references a brand SLUG, not a
 * specific marketplace channel, so it resolves at the REGION grain via regionTarget
 * (channelType null, base = region baseRate). With a null channel the marketplace-
 * shaped scopes (marketplace, marketplace_category, tag_marketplaces) cannot match by
 * construction (the resolver returns false for them), so a channel-specific boost
 * correctly does NOT show on a brand card: it bites a channel, not the brand. The
 * shared resolver and builder are reused AS-IS; there is no second resolution path.
 */
export const projectStoreRate = (
  view: RegionScopeView,
  rules: readonly RateRule[],
  now: number,
  tier: UserTier,
): ResolutionResult =>
  resolveEffectiveRate(regionTarget(view), rules, now, tier);

/**
 * The per-market projection handed to a previewed store card. `resolve` returns the
 * region-level resolution for a slug, or null when the slug does not resolve to a
 * region for this market (a dangling reference or a brand with no region): the card
 * then keeps the 8c behaviour (the raw slug, no rate). `ready` is false while the
 * catalog loads, so a card never flashes a wrong number.
 */
export interface StoreRateProjection {
  resolve: (slug: string) => ResolutionResult | null;
  tier: UserTier;
  ready: boolean;
}

/** The boost-badge text from the winning rule (member-facing label, scopeType fallback). */
export const boostBadgeText = (winner: RateRule): string =>
  `Boosted: ${winner.displayLabel ?? winner.scopeType}`;
